import { BrowserProvider } from 'ethers';
import { CoinbaseWalletSDK } from '@coinbase/wallet-sdk';
import { createAppKit } from '@reown/appkit/react';
import { EthersAdapter } from '@reown/appkit-adapter-ethers';
import { polygon } from '@reown/appkit/networks';

// Clear any stale WalletConnect sessions on load (prevents chain conflicts)
if (typeof window !== 'undefined') {
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('wc@2') || key.startsWith('@w3m') || key.includes('walletconnect'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    if (keysToRemove.length > 0) {
      console.log('🧹 Cleared stale WalletConnect sessions:', keysToRemove.length);
    }
  } catch (e) {
    console.warn('Could not clear WalletConnect cache:', e);
  }
}

class WalletManager {
  constructor() {
    this.coinbaseWallet = null;
    this.coinbaseProvider = null;
    this.selectedWalletType = null;
    this.currentProvider = null;
    this.appKit = null;
    this.walletConnectProvider = null;
    this.isAppKitInitialized = false;
  }

  // Initialize Coinbase Wallet SDK (lazy initialization)
  initializeCoinbaseWallet() {
    if (!this.coinbaseWallet) {
      this.coinbaseWallet = new CoinbaseWalletSDK({
        appName: 'DOCTORdripp Digital Collectibles',
        appLogoUrl: `${window.location.origin}/logo512.png`,
        darkMode: true
      });
    }
    return this.coinbaseWallet;
  }

  // Initialize Reown AppKit (lazy initialization)
  initializeAppKit() {
    if (!this.isAppKitInitialized) {
      const projectId = '332c194864f529e7ac83b7f0dfe71759';
      
      const metadata = {
        name: 'DOCTORdripp Digital Collectibles',
        description: 'Connect your wallet to access DOCTORdripp NFTs',
        url: window.location.origin,
        icons: [`${window.location.origin}/logo512.png`]
      };

      // Create AppKit with Ethers adapter - NFT Dashboard uses Polygon
      // Note: Trust Wallet has a known issue with Reown AppKit WC v2 - awaiting fix from Reown
      this.appKit = createAppKit({
        adapters: [new EthersAdapter()],
        networks: [polygon],
        defaultNetwork: polygon,
        projectId,
        metadata,
        features: {
          analytics: false,
          email: false,
          socials: false,
          swaps: false,
          onramp: false
        },
        themeMode: 'dark',
        themeVariables: {
          '--w3m-accent': '#FF6B35'
        },
        featuredWalletIds: [
          'c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96', // MetaMask
          //'4622a2b2d6af1c9844944291e5e7351a6aa24cd7b23099efac1b2fd875da31a0', // Trust Wallet
          '1ae92b26df02f0abca6304df07debccd18262fdf5fe82daa81593582dac9a369', // Rainbow
          '19177a98252e07ddfc9af2083ba8e07ef627cb6103467ffebb3f8f4205fd7927', // Ledger Live
        ],
        allWallets: 'SHOW',
        allowUnsupportedChain: true,
        enableEIP6963: true,
        enableInjected: true
      });

      this.isAppKitInitialized = true;
      console.log('✅ Reown AppKit initialized');
    }
    return this.appKit;
  }

  // Detect available wallets
  async getAvailableWallets() {
    const wallets = [];
    
    // MetaMask - browser extension wallet
    wallets.push({
      type: 'metamask',
      name: 'MetaMask',
      icon: '/metamask.webp',
      available: true
    });
    
    // WalletConnect - Universal wallet connector (supports 300+ wallets + smart wallets)
    wallets.push({
      type: 'walletconnect',
      name: 'WalletConnect',
      icon: '/walletconnect.webp',
      available: true
    });

    return wallets;
  }

  // EIP-6963 wallet discovery (modern standard to solve provider conflicts)
  discoverWallets() {
    return new Promise((resolve) => {
      const wallets = new Map();
      
      const handleWalletAnnouncement = (event) => {
        if (event.detail?.info?.rdns) {
          wallets.set(event.detail.info.rdns, event.detail);
        }
      };

      window.addEventListener('eip6963:announceProvider', handleWalletAnnouncement);
      window.dispatchEvent(new CustomEvent('eip6963:requestProvider'));
      
      setTimeout(() => {
        window.removeEventListener('eip6963:announceProvider', handleWalletAnnouncement);
        resolve(wallets);
      }, 100);
    });
  }

  // Get MetaMask-specific provider (handles conflicts with other wallets)
  async getMetaMaskProvider() {
    if (!window.ethereum) {
      return null;
    }

    // Method 1: Try EIP-6963 wallet discovery first (most reliable)
    try {
      const discoveredWallets = await this.discoverWallets();
      
      const metamaskWallet = discoveredWallets.get('io.metamask') || 
                           discoveredWallets.get('io.metamask.mobile') ||
                           Array.from(discoveredWallets.values()).find(wallet => 
                             wallet.info.name.toLowerCase().includes('metamask')
                           );
      
      if (metamaskWallet) {
        return metamaskWallet.provider;
      }
    } catch (error) {
      console.warn('EIP-6963 wallet discovery failed:', error.message);
    }

    // Method 2: Check window.ethereum.providers array
    if (window.ethereum.providers && Array.isArray(window.ethereum.providers)) {
      const pureMetaMask = window.ethereum.providers.find(provider => 
        provider.isMetaMask && !provider.isRainbow
      );
      if (pureMetaMask) {
        return pureMetaMask;
      }
    }

    // Method 3: Check for direct MetaMask access
    if (window.MetaMask && typeof window.MetaMask.request === 'function') {
      return window.MetaMask;
    }

    // Method 4: Last resort - use hijacked provider
    if (window.ethereum.isMetaMask) {
      return window.ethereum;
    }
    return null;
  }

  // Connect to MetaMask (enhanced with proper provider selection)
  async connectMetaMask() {
    const metamaskProvider = await this.getMetaMaskProvider();
    
    if (!metamaskProvider) {
      throw new Error("Please install MetaMask.");
    }

    try {
      // If Rainbow has hijacked the provider, try to force MetaMask UI
      if (metamaskProvider.isRainbow && metamaskProvider.isMetaMask) {
        console.log('🦊 Attempting to bypass Rainbow hijack...');
        
        try {
          await metamaskProvider.request({
            method: 'wallet_requestPermissions',
            params: [{ eth_accounts: {} }]
          });
        } catch (permError) {
          console.log('🦊 Permission request failed, continuing with normal flow:', permError.message);
        }
      }

      const [walletAddress] = await metamaskProvider.request({
        method: 'eth_requestAccounts'
      });

      this.selectedWalletType = 'metamask';
      this.currentProvider = new BrowserProvider(metamaskProvider);
      
      return {
        address: walletAddress,
        provider: this.currentProvider,
        walletType: 'metamask'
      };
    } catch (error) {
      if (error.code === 4001 || error.code === 'ACTION_REJECTED') {
        throw new Error("User denied connection request - Please approve the MetaMask connection to continue.");
      }
      throw error;
    }
  }

  // Get Rainbow-specific provider (handles conflicts with other wallets)
  getRainbowProvider() {
    if (!window.ethereum) {
      return null;
    }

    if (window.ethereum.providers) {
      const rainbowProvider = window.ethereum.providers.find(provider => provider.isRainbow);
      if (rainbowProvider) {
        console.log('🌈 Found Rainbow in providers array');
        return rainbowProvider;
      }
    }

    if (window.ethereum.isRainbow) {
      return window.ethereum;
    }
    return null;
  }

  // Helper method to validate Rainbow wallet installation
  async validateRainbowWallet() {
    const rainbowProvider = this.getRainbowProvider();
    
    if (!rainbowProvider) {
      return false;
    }
    
    try {
      await rainbowProvider.request({ method: 'eth_chainId' });
      return { isValid: true, provider: rainbowProvider };
    } catch (error) {
      console.warn('🌈 Rainbow wallet validation failed:', error);
      if (error.message?.includes('chrome.runtime.sendMessage') || error.message?.includes('Extension ID')) {
        if (window.ethereum.isMetaMask) {
          throw new Error("WALLET_CONFLICT_RAINBOW_METAMASK");
        } else if (window.ethereum.isCoinbaseWallet) {
          throw new Error("WALLET_CONFLICT_RAINBOW_COINBASE");
        } else {
          throw new Error("RAINBOW_EXTENSION_ERROR");
        }
      }
      return { isValid: false, provider: null };
    }
  }

  // Connect to Rainbow Wallet
  async connectRainbowWallet() {
    let rainbowProvider;
    try {
      const validationResult = await this.validateRainbowWallet();
      if (!validationResult.isValid) {
        window.open('https://rainbow.me/download', '_blank');
        throw new Error("Rainbow Wallet is not properly installed. Opening download page...");
      }
      rainbowProvider = validationResult.provider;
    } catch (error) {
      if (error.message.startsWith('WALLET_CONFLICT_') || error.message === 'RAINBOW_EXTENSION_ERROR') {
        throw error;
      }
      window.open('https://rainbow.me/download', '_blank');
      throw new Error("Rainbow Wallet validation failed. Please ensure Rainbow Wallet is properly installed.");
    }

    console.log('🌈 Connecting to Rainbow Wallet...');

    try {
      await rainbowProvider.request({ method: 'eth_chainId' });
      
      const [walletAddress] = await rainbowProvider.request({
        method: 'eth_requestAccounts'
      });

      this.selectedWalletType = 'rainbow';
      this.currentProvider = new BrowserProvider(rainbowProvider);
      
      console.log('✅ Rainbow Wallet connected successfully');
      
      return {
        address: walletAddress,
        provider: this.currentProvider,
        walletType: 'rainbow'
      };
    } catch (error) {
      console.error('🌈 Rainbow Wallet connection error:', error);
      
      if (error.message?.includes('chrome.runtime.sendMessage') || error.message?.includes('Extension ID')) {
        if (rainbowProvider.isMetaMask) {
          throw new Error("WALLET_CONFLICT_RAINBOW_METAMASK");
        } else if (rainbowProvider.isCoinbaseWallet) {
          throw new Error("WALLET_CONFLICT_RAINBOW_COINBASE");
        } else {
          throw new Error("RAINBOW_EXTENSION_ERROR");
        }
      }
      
      if (error.code === 4001 || error.code === -32603 || error.code === 'ACTION_REJECTED') {
        throw new Error("User denied connection request - Please approve the Rainbow Wallet connection to continue.");
      }
      throw error;
    }
  }

  // Connect to Magic Eden Wallet (with multichain support + fallback)
  async connectMagicEdenWallet() {
    if (!('magicEden' in window)) {
      window.open('https://wallet.magiceden.io/', '_blank');
      throw new Error("Magic Eden Wallet is not installed. Opening download page...");
    }

    console.log('🔍 Checking Magic Eden providers...');
    
    const multichainProvider = window.magicEden?.multiChain;
    if (multichainProvider) {
      try {
        console.log('🔗 Trying Magic Eden multichain connection...');
        
        const connectionOptions = {
          chains: ['ethereum', 'polygon']
        };
        
        const multichainResponse = await multichainProvider.connect(connectionOptions);
        const ethereumAddressData = multichainResponse.addresses['ethereum'];
        
        if (!ethereumAddressData) {
          throw new Error("No Ethereum address found in Magic Eden multichain response");
        }
        
        const walletAddress = Array.isArray(ethereumAddressData) 
          ? ethereumAddressData[0] 
          : ethereumAddressData;
          
        const ethereumProvider = window.magicEden?.ethereum;
        if (!ethereumProvider) {
          throw new Error("Magic Eden Ethereum provider not found for transaction compatibility");
        }

        this.selectedWalletType = 'magiceden';
        this.currentProvider = new BrowserProvider(ethereumProvider);
        
        console.log('✅ Magic Eden multichain connection successful!');
        
        return {
          address: walletAddress,
          provider: this.currentProvider,
          walletType: 'magiceden',
          isMultichain: true,
          multichainResponse: multichainResponse
        };
      } catch (multichainError) {
        console.log('⚠️ Magic Eden multichain failed, falling back to regular ethereum provider:', multichainError.message);
      }
    }

    const magicProvider = window.magicEden?.ethereum;
    if (!magicProvider?.isMagicEden) {
      window.open('https://wallet.magiceden.io/', '_blank');
      throw new Error("Magic Eden Wallet provider not found. Opening download page...");
    }

    try {
      console.log('🔗 Using Magic Eden regular ethereum provider...');
      
      const accounts = await magicProvider.request({
        method: 'eth_requestAccounts'
      });
      
      const walletAddress = accounts[0];

      this.selectedWalletType = 'magiceden';
      this.currentProvider = new BrowserProvider(magicProvider);
      
      console.log('✅ Magic Eden regular connection successful!');
      
      return {
        address: walletAddress,
        provider: this.currentProvider,
        walletType: 'magiceden',
        isMultichain: false
      };
    } catch (error) {
      if (error.code === 4001 || error.code === 'ACTION_REJECTED') {
        throw new Error("User denied connection request - Please approve the Magic Eden Wallet connection to continue.");
      }
      throw error;
    }
  }

  // Connect to Coinbase Wallet
  async connectCoinbaseWallet() {
    try {
      const coinbaseWallet = this.initializeCoinbaseWallet();
      const ethereum = coinbaseWallet.makeWeb3Provider();

      const [walletAddress] = await ethereum.request({
        method: 'eth_requestAccounts'
      });

      this.selectedWalletType = 'coinbase';
      this.coinbaseProvider = ethereum;
      this.currentProvider = new BrowserProvider(ethereum);

      return {
        address: walletAddress,
        provider: this.currentProvider,
        walletType: 'coinbase'
      };
    } catch (error) {
      if (error.code === 4001 || error.code === 'ACTION_REJECTED') {
        throw new Error("User denied connection request - Please approve the Coinbase Wallet connection to continue.");
      }
      throw error;
    }
  }

  // Connect via Reown AppKit (WalletConnect - supports 300+ wallets including smart wallets)
  async connectWalletConnect() {
    try {
      console.log('🔗 Opening Reown AppKit modal...');
      const appKit = this.initializeAppKit();
      
      // Open the modal
      await appKit.open();
      
      console.log('✅ Modal opened, waiting for user to connect...');
      
      // Wait for connection by polling for the wallet provider
      return new Promise((resolve, reject) => {
        let resolved = false;
        let attempts = 0;
        const maxAttempts = 300; // 5 minutes
        
        const checkConnection = setInterval(async () => {
          attempts++;
          
          try {
            // Check if modal is closed without connecting
            const state = appKit.getState ? appKit.getState() : {};
            
            // Get wallet provider from AppKit
            const walletProvider = appKit.getWalletProvider();
            
            console.log('🔍 Checking connection...', {
              hasProvider: !!walletProvider,
              attempts,
              isOpen: state.open
            });
            
            // If modal was closed and no provider, user cancelled
            if (!state.open && !walletProvider && attempts > 3) {
              resolved = true;
              clearInterval(checkConnection);
              reject(new Error('User cancelled WalletConnect connection'));
              return;
            }
            
            if (walletProvider) {
              try {
                const accounts = await walletProvider.request({ 
                  method: 'eth_accounts' 
                });
                
                if (accounts && accounts.length > 0) {
                  const address = accounts[0];
                  
                  this.walletConnectProvider = walletProvider;
                  this.currentProvider = new BrowserProvider(walletProvider);
                  this.selectedWalletType = 'walletconnect';
                  
                  console.log('✅ WalletConnect connected:', address);
                  
                  resolved = true;
                  clearInterval(checkConnection);
                  appKit.close();
                  
                  resolve({
                    address: address,
                    provider: this.currentProvider,
                    walletType: 'walletconnect'
                  });
                }
              } catch (providerError) {
                console.log('Provider not ready yet:', providerError.message);
              }
            }
            
            if (attempts >= maxAttempts && !resolved) {
              resolved = true;
              clearInterval(checkConnection);
              appKit.close();
              reject(new Error('Connection timeout - wallet did not connect'));
            }
          } catch (error) {
            console.error('Error checking connection:', error);
          }
        }, 1000);
      });
    } catch (error) {
      console.error('❌ WalletConnect error:', error);
      
      if (error.message?.includes('User') || error.message?.includes('timeout')) {
        throw new Error("User cancelled WalletConnect connection");
      }
      
      throw error;
    }
  }

  // Universal connect method - handles all wallets
  async connectWallet(walletType) {
    if (walletType === 'metamask') {
      return await this.connectMetaMask();
    } else if (walletType === 'walletconnect') {
      return await this.connectWalletConnect();
    } else {
      throw new Error('Please use WalletConnect to connect ' + walletType + '. It supports 300+ wallets including Coinbase, Rainbow, and Magic Eden.');
    }
  }

  // Get current provider (maintains compatibility with existing code)
  getCurrentProvider() {
    return this.currentProvider;
  }

  // Get wallet type for conditional logic
  getWalletType() {
    return this.selectedWalletType;
  }

  // Sign message (works with both wallets)
  async signMessage(message) {
    if (!this.currentProvider) {
      throw new Error('No wallet connected');
    }

    try {
      const signer = await this.currentProvider.getSigner();
      return await signer.signMessage(message);
    } catch (error) {
      if (this.selectedWalletType === 'rainbow' && (error.message?.includes('chrome.runtime.sendMessage') || error.message?.includes('Extension ID'))) {
        throw new Error("Rainbow Wallet extension communication error. Please refresh the page and try again, or try reinstalling Rainbow Wallet.");
      }
      throw error;
    }
  }

  // Get current address
  async getCurrentAddress() {
    if (!this.currentProvider) {
      throw new Error('No wallet connected');
    }

    const signer = await this.currentProvider.getSigner();
    return await signer.getAddress();
  }

  // Request account access (compatible with existing window.ethereum calls)
  async requestAccounts() {
    if (this.selectedWalletType === 'metamask') {
      return await window.ethereum.request({
        method: 'eth_requestAccounts'
      });
    } else if (this.selectedWalletType === 'rainbow') {
      return await window.ethereum.request({
        method: 'eth_requestAccounts'
      });
    } else if (this.selectedWalletType === 'magiceden') {
      const multichainProvider = window.magicEden?.multiChain;
      if (multichainProvider && multichainProvider.isConnected) {
        const ethereumProvider = window.magicEden?.ethereum;
        if (ethereumProvider) {
          return await ethereumProvider.request({
            method: 'eth_requestAccounts'
          });
        }
      }
      
      const magicProvider = window.magicEden?.ethereum;
      if (!magicProvider) throw new Error('Magic Eden provider not found');
      return await magicProvider.request({
        method: 'eth_requestAccounts'
      });
    } else if (this.selectedWalletType === 'coinbase') {
      if (!this.coinbaseProvider) throw new Error('Coinbase Wallet provider not found');
      return await this.coinbaseProvider.request({
        method: 'eth_requestAccounts'
      });
    } else if (this.selectedWalletType === 'walletconnect') {
      if (!this.walletConnectProvider) throw new Error('WalletConnect provider not found');
      return await this.walletConnectProvider.request({
        method: 'eth_requestAccounts'
      });
    }
    throw new Error('No wallet connected');
  }

  // Switch network (works with all wallets)
  async switchNetwork(chainId) {
    if (this.selectedWalletType === 'metamask') {
      return await window.ethereum.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId }],
      });
    } else if (this.selectedWalletType === 'rainbow') {
      return await window.ethereum.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId }],
      });
    } else if (this.selectedWalletType === 'magiceden') {
      const magicProvider = window.magicEden?.ethereum;
      if (!magicProvider) throw new Error('Magic Eden provider not found');
      
      try {
        return await magicProvider.request({
          method: "wallet_switchEthereumChain",
          params: [{ chainId }],
        });
      } catch (error) {
        if (error.code === -32601 || error.message?.includes('not supported')) {
          console.log('Magic Eden using native multichain - network switching handled automatically');
          return;
        }
        throw error;
      }
    } else if (this.selectedWalletType === 'coinbase') {
      if (!this.coinbaseProvider) throw new Error('Coinbase Wallet provider not found');
      return await this.coinbaseProvider.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId }],
      });
    } else if (this.selectedWalletType === 'walletconnect') {
      if (!this.walletConnectProvider) throw new Error('WalletConnect provider not found');
      return await this.walletConnectProvider.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId }],
      });
    }
    throw new Error('No wallet connected');
  }

  // Disconnect wallet
  async disconnect() {
    try {
      if (this.selectedWalletType === 'coinbase' && this.coinbaseWallet) {
        await this.coinbaseWallet.disconnect();
        
        if (this.coinbaseProvider && this.coinbaseProvider.disconnect) {
          await this.coinbaseProvider.disconnect();
        }
        
        this.coinbaseWallet = null;
        this.coinbaseProvider = null;
      } else if (this.selectedWalletType === 'magiceden') {
        const multichainProvider = window.magicEden?.multiChain;
        if (multichainProvider && multichainProvider.disconnect) {
          multichainProvider.disconnect();
        }
      } else if (this.selectedWalletType === 'walletconnect') {
        // Disconnect via AppKit
        if (this.appKit && this.appKit.disconnect) {
          await this.appKit.disconnect();
        }
        if (this.walletConnectProvider && this.walletConnectProvider.disconnect) {
        await this.walletConnectProvider.disconnect();
        }
        this.walletConnectProvider = null;
      }
    } catch (error) {
      console.warn('Error during wallet disconnect:', error);
    }
    
    this.selectedWalletType = null;
    this.currentProvider = null;
  }

  // Force cleanup of all wallet state
  forceCleanup() {
    console.log('🧹 Force cleaning wallet state...');
    
    if (this.coinbaseWallet) {
      try {
        this.coinbaseWallet.disconnect();
      } catch (e) {
        console.warn('Error force-disconnecting Coinbase wallet:', e);
      }
      this.coinbaseWallet = null;
    }
    this.coinbaseProvider = null;
    
    try {
      const multichainProvider = window.magicEden?.multiChain;
      if (multichainProvider && multichainProvider.disconnect) {
        multichainProvider.disconnect();
      }
    } catch (e) {
      console.warn('Error force-disconnecting Magic Eden multichain:', e);
    }
    
    // Disconnect AppKit
    if (this.appKit) {
      try {
        this.appKit.disconnect();
      } catch (e) {
        console.warn('Error force-disconnecting AppKit:', e);
      }
    }
    
    if (this.walletConnectProvider) {
      try {
        this.walletConnectProvider.disconnect();
      } catch (e) {
        console.warn('Error force-disconnecting WalletConnect:', e);
      }
      this.walletConnectProvider = null;
    }
    
    this.selectedWalletType = null;
    this.currentProvider = null;
    
    console.log('✅ Wallet state cleaned');
  }

  // Check if we have an active connection
  isConnected() {
    return !!(this.selectedWalletType && this.currentProvider);
  }

  // Get disconnect instructions for UI
  getDisconnectInstructions() {
    if (this.selectedWalletType === 'metamask') {
      return {
        type: 'metamask',
        instructions: 'For complete wallet security, click the DOCTORdripp logo in the top-right corner of your MetaMask extension and select "Disconnect" to fully remove this site\'s access permissions.'
      };
    } else if (this.selectedWalletType === 'rainbow') {
      return {
        type: 'rainbow',
        instructions: 'For complete security, you can also disconnect from your Rainbow Wallet: Click the Polygon symbol in the top-left corner → Connected Apps → Click the red X next to "doctordripp.com". This ensures Rainbow no longer has access to your account.'
      };
    } else if (this.selectedWalletType === 'magiceden') {
      return {
        type: 'magiceden',
        instructions: 'For complete security, you can also disconnect from your Magic Eden Wallet: Settings cog → Connected SITES → Click settings cog next to DOCTORdripp → Click DISCONNECT button at bottom. Magic Eden\'s multichain connection has been automatically disconnected.'
      };
    } else if (this.selectedWalletType === 'coinbase') {
      return {
        type: 'coinbase',
        instructions: 'For complete security, you can also disconnect from your Coinbase Wallet app: Click Settings cog (bottom right) → Click your connected Wallet → Click Connected dapps → Click the 3 dots next to DOCTORdripp → Disconnect. This ensures no future automatic connections.'
      };
    } else if (this.selectedWalletType === 'walletconnect') {
      return {
        type: 'walletconnect',
        instructions: 'WalletConnect session has been disconnected. For complete security, you may also want to manually disconnect from your wallet app if it supports connected dapps management.'
      };
    }
    return null;
  }
}

// Create singleton instance
const walletManager = new WalletManager();

export default walletManager; 
